import React, { useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, Navigation } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Build a numbered SVG marker icon (works on every browser without external assets).
const numberedIcon = (n, color = "#004EEB") =>
  L.divIcon({
    className: "",
    html: `<div style="background:${color};color:#fff;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:'JetBrains Mono',monospace;font-weight:700;font-size:13px;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)">${n}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });

const driverIcon = L.divIcon({
  className: "",
  html: `<div style="background:#16A34A;color:#fff;width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)">●</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

function FitBounds({ points }) {
  const map = useMap();
  React.useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 13);
      return;
    }
    map.fitBounds(points, { padding: [40, 40] });
  }, [map, points]);
  return null;
}

/**
 * Extract one (lat,lng,label,address) point per ticket — first pool delivery
 * with coordinates, falling back to the customer's geocoded address
 * (products-only tickets / pools that couldn't be geocoded).
 */
const ticketPoints = (tickets) =>
  tickets
    .map((t) => {
      const d = (t.pool_deliveries || []).find((x) => x.latitude != null && x.longitude != null);
      if (d) {
        return {
          id: t.id,
          lat: Number(d.latitude),
          lng: Number(d.longitude),
          label: t.business_name || t.customer_name || "Stop",
          address: d.pool_address || "",
        };
      }
      if (t.customer_latitude != null && t.customer_longitude != null) {
        return {
          id: t.id,
          lat: Number(t.customer_latitude),
          lng: Number(t.customer_longitude),
          label: t.business_name || t.customer_name || "Stop",
          address: (t.pool_deliveries || [])[0]?.pool_address || t.customer_address || "",
        };
      }
      return null;
    })
    .filter(Boolean);

/**
 * Build a Google Maps directions URL chaining all the stops as waypoints.
 * Driver gets turn-by-turn for the whole optimized route.
 */
const googleDirectionsUrl = (points, driverLoc) => {
  if (!points || points.length === 0) return null;
  const origin = driverLoc
    ? `${driverLoc.lat},${driverLoc.lng}`
    : `${points[0].lat},${points[0].lng}`;
  const stops = driverLoc ? points : points.slice(1);
  if (stops.length === 0) return null;
  const destination = `${stops[stops.length - 1].lat},${stops[stops.length - 1].lng}`;
  const waypoints = stops.slice(0, -1).map((p) => `${p.lat},${p.lng}`).join("|");
  const u = new URL("https://www.google.com/maps/dir/");
  u.searchParams.set("api", "1");
  u.searchParams.set("origin", origin);
  u.searchParams.set("destination", destination);
  if (waypoints) u.searchParams.set("waypoints", waypoints);
  u.searchParams.set("travelmode", "driving");
  return u.toString();
};

export default function RouteMapDialog({ open, onOpenChange, tickets, sortedTickets, sortMode, driverLoc }) {
  // Points to draw: when sortMode === 'route' we use sortedTickets so the line + numbering
  // reflect the optimized order. Otherwise just show all in date order.
  const points = useMemo(() => ticketPoints(sortedTickets || tickets), [sortedTickets, tickets]);
  const polyline = useMemo(() => {
    if (sortMode !== "route" || points.length < 2) return null;
    const line = points.map((p) => [p.lat, p.lng]);
    if (driverLoc) line.unshift([driverLoc.lat, driverLoc.lng]);
    return line;
  }, [points, sortMode, driverLoc]);

  const boundsPts = useMemo(() => {
    const arr = points.map((p) => [p.lat, p.lng]);
    if (driverLoc) arr.unshift([driverLoc.lat, driverLoc.lng]);
    return arr;
  }, [points, driverLoc]);

  const gmapsUrl = useMemo(() => googleDirectionsUrl(points, driverLoc), [points, driverLoc]);
  const ungeocoded = (sortedTickets || tickets).length - points.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl rounded-sm p-0 overflow-hidden" data-testid="route-map-dialog">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="font-display flex items-center gap-2">
            <Navigation className="w-4 h-4 text-primary" />
            {sortMode === "route" ? "Best route on map" : "All locations on map"}
          </DialogTitle>
          <DialogDescription>
            {points.length} stop{points.length === 1 ? "" : "s"} plotted
            {ungeocoded > 0 ? ` · ${ungeocoded} address${ungeocoded === 1 ? "" : "es"} missing GPS — won't appear` : ""}
            {sortMode === "route" && driverLoc ? " · green dot is your live GPS" : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="h-[60vh] sm:h-[65vh] w-full bg-secondary/40">
          {points.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground text-center p-6">
              No mappable stops — addresses need to be geocoded first.
            </div>
          ) : (
            <MapContainer
              key={`${sortMode}-${points.length}-${driverLoc ? "g" : "ng"}`}
              center={[points[0].lat, points[0].lng]}
              zoom={11}
              style={{ height: "100%", width: "100%" }}
              data-testid="route-map-leaflet"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {polyline && <Polyline positions={polyline} color="#004EEB" weight={4} opacity={0.7} />}
              {driverLoc && (
                <Marker position={[driverLoc.lat, driverLoc.lng]} icon={driverIcon}>
                  <Popup><div className="font-medium text-xs">You are here</div></Popup>
                </Marker>
              )}
              {points.map((p, i) => (
                <Marker key={p.id} position={[p.lat, p.lng]} icon={numberedIcon(i + 1)}>
                  <Popup>
                    <div className="space-y-1">
                      <div className="font-bold text-sm">{i + 1}. {p.label}</div>
                      <div className="text-xs text-muted-foreground">{p.address}</div>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline text-xs inline-flex items-center gap-1"
                      >
                        Open in Google Maps <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </Popup>
                </Marker>
              ))}
              <FitBounds points={boundsPts} />
            </MapContainer>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border bg-secondary/30">
          <div className="text-[11px] text-muted-foreground">
            {sortMode === "route"
              ? "Numbered in the optimized stop order."
              : "Click a marker for address details."}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="route-map-close">Close</Button>
            {gmapsUrl && (
              <Button asChild className="rounded-sm" data-testid="route-map-open-gmaps">
                <a href={gmapsUrl} target="_blank" rel="noopener noreferrer">
                  Open route in Google Maps <ExternalLink className="w-3.5 h-3.5 ml-1" />
                </a>
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
